import React, { Dispatch, SetStateAction, useEffect, useState } from "react";

import InputLabelWrapper from "components/basic/input-label-wrapper/InputLabelWrapper";
import {
  Attribute,
  AttributeData_String,
  AttributeType,
  Maybe,
  MdmDeviceDto,
  MdmObjectDto,
} from "gql/generated/types";
import { Accordion, AccordionTab } from "primereact/accordion";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { T } from "services/lang/LanguageService";
import { StringKey } from "services/lang/keys";

type AdditionalInfoProps<T> = {
  attributes: (Attribute | null)[];
  objectState: T;
  setObjectState: Dispatch<SetStateAction<T>>;
  editMode?: boolean;
};

const AdditionalInfo = <T extends MdmObjectDto | Partial<MdmDeviceDto>>({
  attributes,
  objectState,
  setObjectState,
  editMode = false,
}: AdditionalInfoProps<T>) => {
  const map = new Map(objectState.customFields?.map(field => [field?.key, field?.value]));
  const [attributeMap, setAttributeMap] =
    useState<Map<Maybe<string> | undefined, Maybe<AttributeData_String> | undefined>>(map);

  useEffect(() => {
    setObjectState(prev => ({
      ...prev,
      customFields: [...attributeMap].map(([key, value]) => ({
        key,
        value,
      })),
    }));
  }, [attributeMap, setObjectState]);

  const attributeGroups = attributes.reduce((acc, curr) => {
    const parent = curr?.parent?.toString();
    if (!parent || !curr) return acc;
    const parentAttributes = acc.get(parent);
    if (!parentAttributes) return acc.set(parent, [curr]);
    return acc.set(parent, [...parentAttributes, curr]);
  }, new Map<string, Attribute[]>());

  const renderInput = (body: Attribute) => {
    switch (body.type) {
      case AttributeType.String:
      case AttributeType.Boolean:
      case AttributeType.Enum:
      case AttributeType.Link:
        return (
          <InputText
            value={attributeMap.get(body.id)?.value || ""}
            onChange={e =>
              setAttributeMap(prev => new Map(prev.set(body.id, { type: body.type, value: e.target.value })))
            }
            className="p-inputtext-sm"
            name={body.title}
          />
        );
      case AttributeType.Number:
        return (
          <InputNumber
            value={parseFloat(attributeMap.get(body.id)?.value ?? "0")}
            onChange={e =>
              setAttributeMap(prev => new Map(prev.set(body.id, { type: body.type, value: `${e.value}` })))
            }
            className="p-inputtext-sm"
            name={body.title}
          />
        );
      default:
        return null;
    }
  };

  const accordionTabs = [...attributeGroups].map(([key, value]) => (
    <AccordionTab key={key} header={key}>
      <div className="grid align-items-end">
        {value.map((attr: Attribute) => (
          <InputLabelWrapper
            key={attr.id}
            labelName={T(attr.title as StringKey, { defaultValue: attr.title })}
            col={3}
          >
            {renderInput(attr)}
          </InputLabelWrapper>
        ))}
      </div>
    </AccordionTab>
  ));

  return editMode ? <Accordion className="w-full mt-3">{accordionTabs}</Accordion> : null;
};

export default AdditionalInfo;
